import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { 
  ChevronLeft, 
  ChevronRight, 
  Clock, 
  CalendarDays, 
  Pencil, 
  Trash2, 
  ChevronDown,
  ChevronUp,
  Eye,
  Sun,
  TrendingUp,
  Users,
  CheckCircle,
  XCircle,
  Briefcase
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import LeaveRequestDialog from "../components/attendance/LeaveRequestDialog";
import SundayWorkDialog from "../components/attendance/SundayWorkDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const LEAVE_TYPES = ["Casual", "Sick", "Earned"];

const Attendance = () => {
  const { user } = useAuth();
  const [attendance, setAttendance] = useState([]);
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [showLeavesSection, setShowLeavesSection] = useState(true);
  const [editingLeave, setEditingLeave] = useState(null);
  const [leaveForm, setLeaveForm] = useState({
    start_date: "",
    end_date: "",
    reason: "",
    leave_type: "Casual"
  });
  
  // Admin view state
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [adminAttendance, setAdminAttendance] = useState(null);
  
  // Sunday work modal
  const [showSundayModal, setShowSundayModal] = useState(false);
  const [sundayReason, setSundayReason] = useState("");

  const isAdmin = user?.role === "admin";
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();
  const today = new Date();
  const isTodaySunday = today.getDay() === 0;

  // Generate years for dropdown (current year -5 to +5)
  const years = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);

  useEffect(() => {
    fetchData();
    if (isAdmin) {
      fetchAllUsers();
    }
  }, [currentMonth, currentYear]);

  const fetchAllUsers = async () => {
    try {
      const res = await axios.get(`${API}/users/for-attendance`);
      setAllUsers(res.data);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const fetchAdminAttendance = async (userId) => {
    if (!userId) {
      setAdminAttendance(null);
      return;
    }
    const monthStr = `${currentYear}-${String(currentMonth + 1).padStart(2, "0")}`;
    try {
      const res = await axios.get(`${API}/attendance/admin?user_id=${userId}&month=${monthStr}`);
      setAdminAttendance(res.data);
    } catch (error) {
      console.error("Error fetching admin attendance:", error);
    }
  };

  const handleUserSelect = (userId) => {
    setSelectedUserId(userId);
    fetchAdminAttendance(userId);
  };

  const fetchData = async () => {
    const monthStr = `${currentYear}-${String(currentMonth + 1).padStart(2, "0")}`;
    try {
      const [attendanceRes, todayRes, leavesRes] = await Promise.all([
        axios.get(`${API}/attendance?month=${monthStr}`),
        axios.get(`${API}/attendance/today`),
        axios.get(`${API}/leaves`)
      ]);
      setAttendance(attendanceRes.data);
      setTodayAttendance(todayRes.data);
      setLeaveRequests(leavesRes.data);
      
      // Refresh admin view if a user is selected
      if (selectedUserId) {
        fetchAdminAttendance(selectedUserId);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckIn = async (withReason = false) => {
    try {
      const payload = withReason ? { sunday_reason: sundayReason } : {};
      const response = await axios.post(`${API}/attendance/checkin`, payload);
      
      // Check if Sunday reason is required
      if (response.data.requires_sunday_reason) {
        setShowSundayModal(true);
        return;
      }
      
      toast.success(response.data.is_sunday 
        ? "Checked in for Sunday overtime!" 
        : "Checked in successfully!"
      );
      setShowSundayModal(false);
      setSundayReason("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Check-in failed");
    }
  };

  const handleSundayCheckIn = async () => {
    if (!sundayReason.trim()) {
      toast.error("Please provide a reason for working on Sunday");
      return;
    }
    await handleCheckIn(true);
  };

  const handleCheckOut = async () => {
    try {
      const response = await axios.post(`${API}/attendance/checkout`);
      toast.success(`Checked out! Total today: ${response.data.total_today}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Check-out failed");
    }
  };

  const handleLeaveSubmit = async (e) => {
    e.preventDefault();
    if (!leaveForm.start_date || !leaveForm.end_date || !leaveForm.reason) {
      toast.error("Please fill all fields");
      return;
    }

    try {
      if (editingLeave) {
        await axios.put(`${API}/leaves/${editingLeave.id}/edit`, leaveForm);
        toast.success("Leave request updated successfully!");
      } else {
        await axios.post(`${API}/leaves`, leaveForm);
        toast.success("Leave request submitted! Awaiting admin approval.");
      }
      handleCloseModal();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit leave request");
    }
  };

  const handleEditLeave = (leave) => {
    setEditingLeave(leave);
    setLeaveForm({
      start_date: leave.start_date,
      end_date: leave.end_date,
      reason: leave.reason,
      leave_type: leave.leave_type
    });
    setShowLeaveModal(true);
  };

  const handleDeleteLeave = async (leaveId) => {
    if (!window.confirm("Are you sure you want to delete this leave request?")) {
      return;
    }
    try {
      await axios.delete(`${API}/leaves/${leaveId}`);
      toast.success("Leave request deleted");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete leave request");
    }
  };

  const handleCloseModal = () => {
    setShowLeaveModal(false);
    setEditingLeave(null);
    setLeaveForm({ start_date: "", end_date: "", reason: "", leave_type: "Casual" });
  };

  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Generate calendar days
  const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();

  // Helper to check if a date falls within an approved leave
  const isApprovedLeaveDate = (dateStr) => {
    const checkDate = new Date(dateStr);
    return leaveRequests.some(leave => {
      if (leave.status !== "Approved") return false;
      const startDate = new Date(leave.start_date);
      const endDate = new Date(leave.end_date);
      return checkDate >= startDate && checkDate <= endDate;
    });
  };

  // Check if a date is Sunday
  const isSunday = (year, month, day) => {
    const date = new Date(year, month, day);
    return date.getDay() === 0;
  };

  const calendarDays = [];
  for (let i = 0; i < firstDayOfMonth; i++) {
    calendarDays.push(null);
  }
  for (let day = 1; day <= daysInMonth; day++) {
    const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    const attendanceRecord = attendance.find(a => a.date === dateStr);
    const hasApprovedLeave = isApprovedLeaveDate(dateStr);
    const isDaySunday = isSunday(currentYear, currentMonth, day);
    
    // Determine display status: approved leave overrides everything except actual attendance
    let displayStatus = null;
    if (attendanceRecord?.status === "Present" && attendanceRecord?.check_in) {
      displayStatus = isDaySunday ? "Sunday Work" : "Present";
    } else if (hasApprovedLeave) {
      displayStatus = "Leave";
    } else if (attendanceRecord?.status) {
      displayStatus = attendanceRecord.status;
    } else if (isDaySunday) {
      displayStatus = "Sunday Off";
    }
    
    calendarDays.push({
      day,
      date: dateStr,
      attendance: attendanceRecord,
      displayStatus,
      hasApprovedLeave,
      isSunday: isDaySunday,
      isToday: today.getFullYear() === currentYear && 
               today.getMonth() === currentMonth && 
               today.getDate() === day
    });
  }

  // Stats - only count actual attendance records for the current user
  const presentDays = attendance.filter(a => a.status === "Present" && a.check_in).length;
  const approvedLeaveDays = leaveRequests.filter(l => l.status === "Approved").length;
  
  // Get my leaves vs all leaves (for admin)
  const myLeaves = leaveRequests.filter(l => l.user_id === user?.id);

  // Format today's date
  const todayFormatted = today.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric"
  });

  // Check if currently checked in (has open session)
  const sessions = todayAttendance?.sessions || [];
  const hasOpenSession = sessions.length > 0 && !sessions[sessions.length - 1]?.check_out;
  const canCheckIn = !hasOpenSession;
  const canCheckOut = hasOpenSession;
  const totalSessions = sessions.length;
  const totalMinutes = todayAttendance?.total_minutes || 0;
  const totalHours = Math.floor(totalMinutes / 60);
  const totalMins = totalMinutes % 60;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="attendance-page">
      {/* Mark Attendance Section */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Mark attendance for today ({todayFormatted})
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              {totalSessions > 0 ? (
                <>
                  {totalSessions} session{totalSessions > 1 ? 's' : ''} today
                  {totalMinutes > 0 && ` • Total: ${totalHours}h ${totalMins}m`}
                </>
              ) : (
                "You can mark your attendance for today."
              )}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {canCheckIn && (
              <Button 
                onClick={handleCheckIn} 
                className="bg-prepify-600 hover:bg-prepify-700"
                data-testid="checkin-btn"
              >
                <Clock size={16} className="mr-2" /> 
                {totalSessions > 0 ? "Restart Work" : "Check In"}
              </Button>
            )}
            {canCheckOut && (
              <Button 
                onClick={handleCheckOut} 
                variant="outline"
                data-testid="checkout-btn"
              >
                <Clock size={16} className="mr-2" /> Check Out
              </Button>
            )}
            <Button 
              variant="outline" 
              className="bg-prepify-600 text-white hover:bg-prepify-700"
              onClick={() => setShowLeavesSection(!showLeavesSection)}
            >
              <Eye size={16} className="mr-2" /> View leaves taken
            </Button>
          </div>
        </div>
        
        {/* Show sessions breakdown */}
        {sessions.length > 0 && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <p className="text-sm font-medium text-slate-700 mb-2">Today's Sessions:</p>
            <div className="flex flex-wrap gap-2">
              {sessions.map((session, idx) => (
                <span 
                  key={idx} 
                  className={`text-xs px-2 py-1 rounded-full ${
                    session.check_out 
                      ? "bg-green-50 text-green-700" 
                      : "bg-blue-50 text-blue-700"
                  }`}
                >
                  Session {idx + 1}: {session.check_in} - {session.check_out || "ongoing"}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Calendar Section */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        {/* Calendar Header */}
        <div className="flex items-center gap-4 mb-6">
          <button onClick={goToPreviousMonth} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
            <ChevronLeft size={20} />
          </button>
          <button onClick={goToNextMonth} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
            <ChevronRight size={20} />
          </button>
          
          <Select 
            value={String(currentMonth)} 
            onValueChange={(v) => setCurrentDate(new Date(currentYear, parseInt(v), 1))}
          >
            <SelectTrigger className="w-32 bg-slate-800 text-white border-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MONTHS.map((month, idx) => (
                <SelectItem key={idx} value={String(idx)}>{month}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select 
            value={String(currentYear)} 
            onValueChange={(v) => setCurrentDate(new Date(parseInt(v), currentMonth, 1))}
          >
            <SelectTrigger className="w-24 bg-slate-800 text-white border-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map((year) => (
                <SelectItem key={year} value={String(year)}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button 
            variant="outline" 
            onClick={goToToday}
            className="bg-slate-800 text-white hover:bg-slate-700 border-0"
          >
            Today
          </Button>
        </div>

        {/* Day Headers */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {DAYS.map((day, idx) => (
            <div 
              key={day} 
              className={`text-center text-sm font-medium py-2 ${
                idx === 0 ? "text-red-500" : "text-slate-500"
              }`}
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-1">
          {calendarDays.map((dayData, idx) => (
            <div
              key={idx}
              className={`h-14 flex flex-col items-center justify-center rounded-lg text-sm transition-colors relative ${
                !dayData ? "bg-transparent" :
                dayData.isToday ? "border-2 border-slate-800 font-semibold" :
                dayData.displayStatus === "Sunday Work" ? "bg-purple-100 text-purple-700" :
                dayData.displayStatus === "Present" ? "bg-green-50 text-green-700" :
                dayData.displayStatus === "Leave" ? "bg-amber-50 text-amber-700" :
                dayData.displayStatus === "Absent" ? "bg-red-50 text-red-700" :
                dayData.displayStatus === "Sunday Off" ? "bg-slate-100 text-slate-400" :
                dayData?.isSunday ? "bg-slate-100 text-slate-400" :
                "hover:bg-slate-50"
              }`}
              title={dayData?.displayStatus || (dayData?.isSunday ? "Sunday (Off)" : "")}
            >
              {dayData && (
                <>
                  <span>{dayData.day}</span>
                  {dayData.displayStatus === "Sunday Work" && (
                    <Sun size={10} className="text-purple-500 mt-0.5" />
                  )}
                </>
              )}
            </div>
          ))}
        </div>

        {/* Legend */}
        <div className="mt-4 flex flex-wrap gap-4 text-xs">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-green-50 border border-green-200"></div>
            <span className="text-slate-600">Present</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-amber-50 border border-amber-200"></div>
            <span className="text-slate-600">Leave</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-slate-100 border border-slate-200"></div>
            <span className="text-slate-600">Sunday (Off)</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-purple-100 border border-purple-200"></div>
            <span className="text-slate-600">Sunday Work (OT)</span>
          </div>
        </div>

        {/* Apply Leave Button */}
        <div className="mt-6">
          <Button 
            onClick={() => setShowLeaveModal(true)}
            className="bg-prepify-600 hover:bg-prepify-700"
            data-testid="apply-leave-btn"
          >
            Apply Leave
          </Button>
        </div>
      </div>

      {/* Attendance Table */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Attendance
          </h2>
          <p className="text-sm text-slate-500">
            To update your attendance data, please click on the edit button next to each date.
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-800 text-white">
                <th className="text-left py-3 px-4 font-medium rounded-tl-lg">Date</th>
                <th className="text-left py-3 px-4 font-medium">Status</th>
                <th className="text-left py-3 px-4 font-medium">Sessions</th>
                <th className="text-left py-3 px-4 font-medium">First In</th>
                <th className="text-left py-3 px-4 font-medium">Last Out</th>
                <th className="text-left py-3 px-4 font-medium">Total Time</th>
                <th className="text-center py-3 px-4 font-medium rounded-tr-lg">Edit</th>
              </tr>
            </thead>
            <tbody>
              {/* Generate rows for each day of the month */}
              {Array.from({ length: daysInMonth }, (_, i) => {
                const day = i + 1;
                const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                const record = attendance.find(a => a.date === dateStr);
                const dateObj = new Date(currentYear, currentMonth, day);
                const formattedDate = dateObj.toLocaleDateString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric" });
                
                // Get sessions and calculate total time
                const recordSessions = record?.sessions || [];
                const sessionCount = recordSessions.length;
                const totalMins = record?.total_minutes || 0;
                let duration = "-";
                if (totalMins > 0) {
                  const hours = Math.floor(totalMins / 60);
                  const mins = totalMins % 60;
                  duration = `${hours}h ${mins}m`;
                } else if (record?.check_in && record?.check_out) {
                  // Fallback for old data without sessions
                  const [inH, inM] = record.check_in.split(":").map(Number);
                  const [outH, outM] = record.check_out.split(":").map(Number);
                  const calcMins = (outH * 60 + outM) - (inH * 60 + inM);
                  if (calcMins > 0) {
                    const hours = Math.floor(calcMins / 60);
                    const mins = calcMins % 60;
                    duration = `${hours}h ${mins}m`;
                  }
                }

                return (
                  <tr key={day} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-4 font-medium">{formattedDate}</td>
                    <td className="py-3 px-4">
                      {record?.status ? (
                        <span className={`badge ${
                          record.status === "Present" ? "badge-success" :
                          record.status === "Leave" ? "badge-warning" : "badge-danger"
                        }`}>
                          {record.status}
                        </span>
                      ) : "-"}
                    </td>
                    <td className="py-3 px-4">
                      {sessionCount > 0 ? (
                        <span className="text-sm text-slate-600">{sessionCount} session{sessionCount > 1 ? 's' : ''}</span>
                      ) : "-"}
                    </td>
                    <td className="py-3 px-4 font-mono text-green-600">{record?.check_in || "-"}</td>
                    <td className="py-3 px-4 font-mono text-prepify-600">{record?.check_out || "-"}</td>
                    <td className="py-3 px-4 font-medium">{duration}</td>
                    <td className="py-3 px-4 text-center">
                      <button className="p-2 text-slate-400 hover:text-prepify-600 hover:bg-prepify-50 rounded-lg transition-colors">
                        <Pencil size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Leave Requests Section - Collapsible */}
      {leaveRequests.length > 0 && showLeavesSection && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="leave-requests-section">
          <button 
            onClick={() => setShowLeavesSection(!showLeavesSection)}
            className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
          >
            <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {isAdmin ? "All Leave Requests" : "My Leave Requests"} ({leaveRequests.length})
            </h2>
            {showLeavesSection ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </button>
          
          {showLeavesSection && (
            <div className="border-t border-slate-100">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50">
                      {isAdmin && <th className="text-left py-3 px-4 font-medium text-slate-600">Employee</th>}
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Type</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-600">From</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-600">To</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Reason</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Status</th>
                      <th className="text-center py-3 px-4 font-medium text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaveRequests.map((leave) => {
                      const canEdit = leave.status === "Pending" && (leave.user_id === user?.id || isAdmin);
                      return (
                        <tr key={leave.id} className="border-b border-slate-100 hover:bg-slate-50">
                          {isAdmin && (
                            <td className="py-3 px-4 font-medium">{leave.user_name}</td>
                          )}
                          <td className="py-3 px-4">{leave.leave_type}</td>
                          <td className="py-3 px-4">{new Date(leave.start_date).toLocaleDateString("en-IN")}</td>
                          <td className="py-3 px-4">{new Date(leave.end_date).toLocaleDateString("en-IN")}</td>
                          <td className="py-3 px-4 text-slate-500 max-w-xs truncate">{leave.reason}</td>
                          <td className="py-3 px-4">
                            <span className={`badge ${
                              leave.status === "Approved" ? "badge-success" :
                              leave.status === "Rejected" ? "badge-danger" : "badge-warning"
                            }`}>
                              {leave.status}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center justify-center gap-1">
                              {canEdit && (
                                <>
                                  <button
                                    onClick={() => handleEditLeave(leave)}
                                    className="p-2 text-slate-400 hover:text-prepify-600 hover:bg-prepify-50 rounded-lg transition-colors"
                                    title="Edit"
                                    data-testid={`edit-leave-${leave.id}`}
                                  >
                                    <Pencil size={16} />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteLeave(leave.id)}
                                    className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                    title="Delete"
                                    data-testid={`delete-leave-${leave.id}`}
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </>
                              )}
                              {!canEdit && <span className="text-slate-300">-</span>}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Admin Attendance Panel */}
      {isAdmin && (
        <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="admin-attendance-panel">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Users className="inline-block mr-2 mb-1" size={20} />
              Team Attendance Overview
            </h2>
            <div className="flex items-center gap-3">
              <Select value={selectedUserId} onValueChange={handleUserSelect}>
                <SelectTrigger className="w-64" data-testid="user-select">
                  <SelectValue placeholder="Select employee to view..." />
                </SelectTrigger>
                <SelectContent>
                  {allUsers.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name} ({u.role})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {selectedUserId && adminAttendance && (
            <>
              {/* Month Summary Header */}
              <div className="mb-6 p-4 bg-gradient-to-r from-prepify-50 to-prepify-100 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-slate-900">
                      {MONTHS[currentMonth]} {currentYear} Summary
                    </h3>
                    <p className="text-sm text-slate-600 mt-1">
                      Total Working Days: {adminAttendance.summary.total_working_days} 
                      <span className="text-slate-400 ml-2">(excl. {adminAttendance.summary.sundays_in_month} Sundays)</span>
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-prepify-600">
                      {adminAttendance.summary.attendance_percentage}%
                    </p>
                    <p className="text-sm text-slate-500">Attendance</p>
                  </div>
                </div>
              </div>

              {/* Summary Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
                <div className="bg-green-50 rounded-lg p-4 text-center border border-green-100">
                  <CheckCircle className="mx-auto mb-2 text-green-500" size={24} />
                  <p className="text-2xl font-bold text-green-600">{adminAttendance.summary.present_days}</p>
                  <p className="text-xs text-green-700">Present Days</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-4 text-center border border-amber-100">
                  <CalendarDays className="mx-auto mb-2 text-amber-500" size={24} />
                  <p className="text-2xl font-bold text-amber-600">{adminAttendance.summary.leave_days}</p>
                  <p className="text-xs text-amber-700">Leave Days</p>
                </div>
                <div className="bg-red-50 rounded-lg p-4 text-center border border-red-100">
                  <XCircle className="mx-auto mb-2 text-red-500" size={24} />
                  <p className="text-2xl font-bold text-red-600">{adminAttendance.summary.absent_days}</p>
                  <p className="text-xs text-red-700">Absent Days</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4 text-center border border-purple-100">
                  <Sun className="mx-auto mb-2 text-purple-500" size={24} />
                  <p className="text-2xl font-bold text-purple-600">{adminAttendance.summary.sunday_work_days}</p>
                  <p className="text-xs text-purple-700">Sunday Work (OT)</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-4 text-center border border-blue-100">
                  <Clock className="mx-auto mb-2 text-blue-500" size={24} />
                  <p className="text-2xl font-bold text-blue-600">{adminAttendance.summary.total_hours}h</p>
                  <p className="text-xs text-blue-700">Total Hours</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4 text-center border border-slate-200">
                  <TrendingUp className="mx-auto mb-2 text-slate-500" size={24} />
                  <p className="text-2xl font-bold text-slate-600">{adminAttendance.summary.avg_hours_per_day}h</p>
                  <p className="text-xs text-slate-700">Avg/Day</p>
                </div>
              </div>

              {/* Attendance Details Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-800 text-white">
                      <th className="text-left py-3 px-4 font-medium rounded-tl-lg">Date</th>
                      <th className="text-left py-3 px-4 font-medium">Day</th>
                      <th className="text-left py-3 px-4 font-medium">Status</th>
                      <th className="text-left py-3 px-4 font-medium">Sessions</th>
                      <th className="text-left py-3 px-4 font-medium">First In</th>
                      <th className="text-left py-3 px-4 font-medium">Last Out</th>
                      <th className="text-left py-3 px-4 font-medium">Total Time</th>
                      <th className="text-left py-3 px-4 font-medium rounded-tr-lg">Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminAttendance.attendance.map((record) => {
                      const totalMins = record.total_minutes || 0;
                      const hours = Math.floor(totalMins / 60);
                      const mins = totalMins % 60;
                      const duration = totalMins > 0 ? `${hours}h ${mins}m` : "-";
                      const sessions = record.sessions || [];
                      const dateObj = new Date(record.date);
                      const dayName = dateObj.toLocaleDateString("en-IN", { weekday: "short" });
                      const isSundayRecord = dateObj.getDay() === 0;
                      
                      return (
                        <tr key={record.date} className={`border-b border-slate-100 hover:bg-slate-50 ${isSundayRecord ? "bg-purple-50/50" : ""}`}>
                          <td className="py-3 px-4 font-medium">
                            {dateObj.toLocaleDateString("en-IN", { 
                              day: "2-digit", month: "2-digit", year: "numeric" 
                            })}
                          </td>
                          <td className={`py-3 px-4 ${isSundayRecord ? "text-purple-600 font-medium" : ""}`}>
                            {dayName}
                          </td>
                          <td className="py-3 px-4">
                            <span className={`badge ${
                              record.status === "Present" && isSundayRecord ? "bg-purple-100 text-purple-700" :
                              record.status === "Present" ? "badge-success" :
                              record.status === "Leave" ? "badge-warning" : "badge-danger"
                            }`}>
                              {record.status === "Present" && isSundayRecord ? "Sunday OT" : record.status}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            {sessions.length > 0 ? `${sessions.length} session${sessions.length > 1 ? 's' : ''}` : "-"}
                          </td>
                          <td className="py-3 px-4 font-mono text-green-600">{record.check_in || "-"}</td>
                          <td className="py-3 px-4 font-mono text-prepify-600">{record.check_out || "-"}</td>
                          <td className="py-3 px-4 font-medium">{duration}</td>
                          <td className="py-3 px-4 text-sm text-slate-500">
                            {record.sunday_reason || (record.is_sunday_work ? "Sunday Overtime" : "-")}
                          </td>
                        </tr>
                      );
                    })}
                    {adminAttendance.attendance.length === 0 && (
                      <tr>
                        <td colSpan={8} className="py-8 text-center text-slate-500">
                          No attendance records found for this month
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {!selectedUserId && (
            <div className="text-center py-12 text-slate-500">
              <Users size={48} className="mx-auto mb-4 text-slate-300" />
              <p className="font-medium">Select an employee from the dropdown</p>
              <p className="text-sm mt-1">to view their attendance details and statistics</p>
            </div>
          )}
        </div>
      )}

      {/* Leave Request Modal */}
      <LeaveRequestDialog
        open={showLeaveModal}
        onClose={handleCloseModal}
        editing={editingLeave}
        form={leaveForm}
        setForm={setLeaveForm}
        onSubmit={handleLeaveSubmit}
      />

      {/* Sunday Work Modal */}
      <SundayWorkDialog
        open={showSundayModal}
        onClose={() => { setShowSundayModal(false); setSundayReason(""); }}
        reason={sundayReason}
        setReason={setSundayReason}
        onConfirm={handleSundayCheckIn}
      />
    </div>
  );
};

export default Attendance;
